import { type User } from '@/features/iam/users/User'
import { type Observable } from 'rxjs'
import { type Id } from '@/infra/id'
import type { Workspace } from '@/features/iam/workspaces/Workspace'
import { type SnackbarErrorHandlerFactory } from '@/features/shell'
import { createInternalFeatureLoader } from '@/utils/feature-loading-util'

/**
 * Options for the {@link AccountFeature}.
 */
export interface AccountFeatureOptions {
  token$: Observable<string>
  createSnackbarErrorHandler: SnackbarErrorHandlerFactory
  getCurrentUser: () => User
  getWorkspace: (workspaceId: Id) => Workspace
}

/**
 * Delegate for prompting for terms acceptance when needed.
 */
export type EnsureTermsAcceptanceFn = (
  workspaceId: Id,
  signal?: AbortSignal,
) => Promise<EnsureTermsAcceptanceResult>

/**
 * Delegate for fetching terms acceptance information for the current user in
 * the specified workspace.
 */
export type FetchTermsAcceptanceInfoFn = (
  workspaceId: Id,
  signal?: AbortSignal,
) => Promise<TermsAcceptanceInfo>

/**
 * Whether the terms have been accepted or not, or if not even applicable.
 */
export type TermsAcceptanceInfo = 'Accepted' | 'NotAccepted' | 'NotApplicable'

/**
 * The outcome of calling an {@link EnsureTermsAcceptanceFn} function.
 */
export type EnsureTermsAcceptanceResult = 'Accepted' | 'Declined' | 'Aborted'

/**
 * The outcome of calling an {@link EnsureSection7216AcceptanceFn} function.
 */
export type EnsureSection7216AcceptanceResult =
  | 'Accepted'
  | 'Declined'
  | 'Aborted'

/**
 * Options for the {@link EnsureSection7216AcceptanceFn} function.
 */
export interface EnsureSection7216AcceptanceOptions {
  isSpouseRequired: boolean
  signal?: AbortSignal
}

/**
 * Delegate for prompting for section 7216 acceptance.
 */
export type EnsureSection7216AcceptanceFn = (
  options: EnsureSection7216AcceptanceOptions,
) => Promise<EnsureSection7216AcceptanceResult>

/**
 * Everything related to managing the current user's profile.
 */
export type AccountFeature = ReturnType<typeof AccountFeature>

export function AccountFeature(options: AccountFeatureOptions) {
  const internal = createInternalFeatureLoader(
    () => import('./internal'),
    (module) => module.InternalAccountFeature(options),
  )

  const ensureTermsAcceptance: EnsureTermsAcceptanceFn = internal.wrap(
    (m) => m.ensureTermsAcceptance,
  )

  const fetchTermsAcceptance: FetchTermsAcceptanceInfoFn = internal.wrap(
    (m) => m.fetchTermsAcceptance,
  )

  const ensureSection7216Acceptance: EnsureSection7216AcceptanceFn =
    internal.wrap((m) => m.ensureSection7216Acceptance)

  return {
    // Internal
    internal,

    // Public
    ensureTermsAcceptance,
    fetchTermsAcceptance,
    ensureSection7216Acceptance,
  }
}
